import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { ContentContainerType } from 'types/enums';
import { LabelV2Version, SoundCloudVersion } from 'types/releases';
import {
  getOpenInNewTabMenuOption,
  useContextMenu,
} from 'app/components/ChakraTableList';
import { ReactComponent as AudioIcon } from 'resources/images/audio.svg';
import { ChakraEmptyList } from 'app/components/Core';
import { ListDragLayer } from 'app/components/DraggableListRow';
import { GenericObject } from 'types/generic.types';
import { routes } from 'app/routing/routesConfig';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { useVersionsListReorder } from 'app/components/Releases/hooks';
import { useAppSelector } from 'app/store';
import { getVersionTypes } from 'app/store/Releases/selectors';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { useCallback, useRef } from 'react';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { useLabelV2VersionListColumns } from './utils';
import { LabelV2VersionTableRow } from './LabelV2VersionTableRow';

export interface ReleaseVersionsListComponentProps {
  loading: boolean;
  versions: LabelV2Version[];
  canDeleteRelease: boolean;
  releaseGuid: string;
  onDelete: (data: {
    versionLabel?: string;
    versionGuid: string;
    songGuid: string;
  }) => void;
  currentlyReplacingAudio: GenericObject<boolean>;
}
export const LabelV2VersionsListComponent = ({
  versions,
  loading,
  canDeleteRelease,
  releaseGuid,
  onDelete,
  currentlyReplacingAudio,
}: ReleaseVersionsListComponentProps) => {
  const versionTypesDDL = useAppSelector(getVersionTypes);
  const {
    tooltipTitle,
    onRowReorder,
    rows,
    onDragging,
  } = useVersionsListReorder(versions);

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'labelV2-versions';
  const totalWidth = useTotalWidth(ref);
  const columns = useLabelV2VersionListColumns(totalWidth);
  const contextMenuProps = useContextMenu();

  const rowRenderer = useCallback(
    (row: Row<SoundCloudVersion>, index) => {
      const version = row.original;
      const { song, title } = version;

      const versionDetailsPath = routes.releaseVersionDetails.path
        .replace(':guid', releaseGuid)
        .replace(':versionGuid', version.guid);

      const options = [
        ...getOpenInNewTabMenuOption(versionDetailsPath),
        ...(canDeleteRelease
          ? [
              {
                title: 'Delete',
                value: 'DELETE',
                onClick: () => {
                  onDelete({
                    versionLabel: title || song.title,
                    versionGuid: version.guid,
                    songGuid: song.guid,
                  });
                },
              },
            ]
          : []),
      ];

      return (
        <LabelV2VersionTableRow
          key={row.id}
          row={row}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
          index={index}
          currentlyReplacingAudio={currentlyReplacingAudio}
          versionTypesDDL={versionTypesDDL}
          onRowReorder={onRowReorder}
          onDragging={onDragging}
          href={versionDetailsPath}
        />
      );
    },
    [
      canDeleteRelease,
      contextMenuProps,
      currentlyReplacingAudio,
      onDelete,
      onDragging,
      onRowReorder,
      releaseGuid,
      versionTypesDDL,
    ]
  );

  if (!loading && rows.length === 0) {
    return (
      <ChakraEmptyList
        icon={<AudioIcon role="img" />}
        title="No audio files added"
        primaryText="Add an audio file to your Release to fetch all necessary song info and credits."
      />
    );
  }

  return (
    <ContentContainer type={ContentContainerType.LIST} isViewContent>
      <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
        <StickyTable<SoundCloudVersion>
          tableKey={tableKey}
          ref={ref}
          data={rows}
          columns={columns}
          rowRenderer={rowRenderer}
          loading={loading}
          contextMenuProps={contextMenuProps}
        />
        <ListDragLayer title={tooltipTitle || ''} />
      </DndProvider>
    </ContentContainer>
  );
};
